import { AfterViewInit, Component, HostListener, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { AuthService } from '../../../shared/services/auth.service';
import { SidenavService } from '../../../shared/services/sidenav.service';
import { LanguageService } from '../../../shared/services/language.service';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ApiEstimativaService } from 'src/app/modules/shared/services/api-estimativa.service';
import { ScrollControlService } from 'src/app/modules/shared/services/scroll-control.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

type HelpTopicKey = string;

interface HelpUseCase {
  title: string;
  objective: string;
  whenToUse: string;
  section: HelpTopicKey;
  audiences: QuickGuideRole[];
  tags: string[];
  steps: string[];
  validation: string;
  commonError: string;
}

interface HelpTopic {
  key: HelpTopicKey;
  label: string;
  featured: boolean;
}

type QuickGuideRole = 'all' | 'estimator' | 'reviewer' | 'admin';

@Component({
  standalone: false,
  selector: 'app-faqs',
  templateUrl: './faqs.component.html',
  styleUrls: ['./faqs.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class FaqsComponent implements OnInit, AfterViewInit, OnDestroy {
  variables: any;
  isOpen: boolean = false;
  shouldPinSidenav: boolean = false;
  dataSource = new MatTableDataSource<any>([]);
  colorWhite = '#fff';
  activeHelpSection: HelpTopicKey = 'topic-01';
  helpSearchTerm: string = '';
  showAllTopics: boolean = false;
  selectedGuideRole: QuickGuideRole = 'all';
  autoDetectedGuideRole: QuickGuideRole = 'all';
  hasManualRoleSelection: boolean = false;
  private topicObserver: IntersectionObserver | null = null;

  get roleFilterOptions() {
    return [
      { key: 'all' as const, label: this.variables?.helpRoleAll || 'Todos' },
      { key: 'estimator' as const, label: this.variables?.helpRoleEstimator || 'Estimador' },
      { key: 'reviewer' as const, label: this.variables?.helpRoleReviewer || 'Revisor' },
      { key: 'admin' as const, label: this.variables?.helpRoleAdmin || 'Admin' },
    ];
  }

  helpSections: HelpTopic[] = [
    { key: 'topic-01', label: '1. Cómo está organizada la aplicación', featured: true },
    { key: 'topic-02', label: '2. Flujo recomendado de uso', featured: true },
    { key: 'topic-03', label: '3. Pantallas principales', featured: true },
    { key: 'topic-04', label: '4. Uso de la pantalla de estimación', featured: true },
    { key: 'topic-05', label: '5. Estados y ciclo de revisión', featured: true },
    { key: 'topic-06', label: '6. Conceptos clave', featured: false },
    { key: 'topic-07', label: '7. Reglas prácticas', featured: false },
    { key: 'topic-08', label: '8. Problemas frecuentes', featured: true },
    { key: 'topic-09', label: '9. Buenas prácticas de operación', featured: false },
    { key: 'topic-10', label: '10. Manual completo por pantalla', featured: true },
    { key: 'topic-11', label: '11. Procedimientos operativos', featured: true },
    { key: 'topic-12', label: '12. Qué puede hacer cada rol', featured: false },
    { key: 'topic-13', label: '13. Glosario funcional', featured: false },
    { key: 'topic-14', label: '14. Escenarios reales de uso', featured: false },
    { key: 'topic-15', label: '15. Validaciones y mensajes', featured: true },
    { key: 'topic-16', label: '16. Checklist de cierre', featured: false },
    { key: 'topic-17', label: '17. Matriz de permisos por acción', featured: false },
    { key: 'topic-18', label: '18. Definición de campos clave', featured: false },
    { key: 'topic-19', label: '19. Transiciones de estado', featured: false },
    { key: 'topic-20', label: '20. Significado de cada acción', featured: true },
    { key: 'topic-21', label: '21. Errores comunes', featured: true },
    { key: 'topic-22', label: '22. Casos de uso avanzados', featured: false },
    { key: 'topic-23', label: '23. Integración con herramientas externas', featured: false },
    { key: 'topic-24', label: '24. Referencia rápida por pantalla', featured: false },
    { key: 'topic-25', label: '25. Preguntas frecuentes', featured: true }
  ];

  helpUseCases: HelpUseCase[] = [
    {
      title: 'Quiero crear una estimación nueva',
      objective: 'Crear una estimación completa y guardarla sin perder información.',
      whenToUse: 'Cuando arrancas un trabajo y necesitas dejar una estimación preparada para revisión.',
      section: 'topic-11',
      audiences: ['estimator', 'admin'],
      tags: ['crear', 'nueva', 'estimacion', 'fase 1', 'inicio'],
      steps: [
        'Abre Mis estimaciones y pulsa Nueva estimación.',
        'Completa nombre, proyecto, catálogo y metodología en Fase 1.',
        'Valida obligatorios y guarda en curso para no perder progreso.'
      ],
      validation: 'La estimación queda en estado En curso o Completa y aparece en el listado.',
      commonError: 'Olvidar seleccionar catálogo o metodología, lo que bloquea el guardado.'
    },
    {
      title: 'No me deja guardar o completar',
      objective: 'Resolver bloqueo de guardado con el menor número de comprobaciones.',
      whenToUse: 'Si el sistema bloquea el guardado o muestra validaciones.',
      section: 'topic-15',
      audiences: ['estimator', 'admin'],
      tags: ['guardar', 'completar', 'error', 'validacion', 'obligatorio'],
      steps: [
        'Revisa campos obligatorios y formato de valores numéricos.',
        'Confirma que el proyecto esté activo y el catálogo sea válido.',
        'Si persiste, revisa el bloque de errores comunes y el código de error mostrado.'
      ],
      validation: 'El botón Guardar/Completar vuelve a estar habilitado y no aparecen validaciones rojas.',
      commonError: 'Complejidad o esfuerzo informado en formato incorrecto.'
    },
    {
      title: 'Necesito certificar y enviar a revisión',
      objective: 'Dejar la estimación lista para aprobación sin devoluciones.',
      whenToUse: 'Cuando la estimación ya está completa y debe pasar a validación.',
      section: 'topic-20',
      audiences: ['estimator', 'admin'],
      tags: ['certificar', 'revision', 'aprobar', 'estado'],
      steps: [
        'Verifica subtareas, perfiles, riesgos y comentarios pendientes.',
        'Pasa a estado Completa si aún no lo está.',
        'Ejecuta Certificar y confirma que el estado final sea Certificada.'
      ],
      validation: 'El estado mostrado es Certificada y se registra en notificaciones/histórico.',
      commonError: 'Intentar certificar sin tener permiso o con datos obligatorios incompletos.'
    },
    {
      title: 'Me pidieron cambios en una estimación',
      objective: 'Corregir rápido y reenviar a revisión con trazabilidad.',
      whenToUse: 'Cuando recibes devolución de revisor y debes corregir rápido.',
      section: 'topic-14',
      audiences: ['estimator', 'admin'],
      tags: ['cambios', 'rechazo', 'corregir', 'retrabajo'],
      steps: [
        'Abre la estimación y revisa el comentario de solicitud de cambios.',
        'Corrige subtareas, perfiles o riesgos indicados por el revisor.',
        'Guarda y vuelve a certificar para nueva revisión.'
      ],
      validation: 'La estimación vuelve a estado Certificada con comentario de ajuste registrado.',
      commonError: 'Reenviar sin responder al comentario concreto del revisor.'
    },
    {
      title: 'No encuentro una estimación',
      objective: 'Localizar una estimación en menos de un minuto.',
      whenToUse: 'Si la estimación parece desaparecer del dashboard.',
      section: 'topic-08',
      audiences: ['estimator', 'reviewer', 'admin'],
      tags: ['buscar', 'filtros', 'tenant', 'dashboard', 'no encuentro'],
      steps: [
        'Limpia filtros y busca por nombre parcial del proyecto o estimación.',
        'Confirma tenant activo y rango de fechas aplicado.',
        'Si sigue sin aparecer, revisa estado o asociación al proyecto.'
      ],
      validation: 'La estimación aparece en Dashboard o en detalle de proyecto.',
      commonError: 'Buscar en tenant distinto o con filtros de fecha cerrados.'
    },
    {
      title: 'Quiero exportar a Excel o Jira',
      objective: 'Generar salida externa de la estimación para compartirla.',
      whenToUse: 'Cuando necesitas compartir el resultado fuera de Estimator.',
      section: 'topic-23',
      audiences: ['reviewer', 'admin'],
      tags: ['exportar', 'excel', 'jira', 'resultado'],
      steps: [
        'Abre la pantalla Resultado de estimación.',
        'Pulsa Exportar Excel para descargar o Exportar Jira si aplica.',
        'Si Jira no aparece, valida metodología Agile y estado certificado.'
      ],
      validation: 'Se descarga archivo XLS o se confirma envío correcto a Jira.',
      commonError: 'Esperar exportación Jira en estimaciones no Agile o no certificadas.'
    },
    {
      title: 'Quiero aprobar o rechazar una estimación',
      objective: 'Tomar decisión de revisión de forma trazable.',
      whenToUse: 'Cuando actúas como revisor/aprobador en estado Certificada.',
      section: 'topic-20',
      audiences: ['reviewer', 'admin'],
      tags: ['aprobar', 'rechazar', 'revisor', 'decision'],
      steps: [
        'Abre Resultado y revisa desglose de esfuerzo/coste y comentarios.',
        'Pulsa Aprobar o Rechazar según calidad y criterios definidos.',
        'Si rechazas, añade comentario concreto de por qué y qué corregir.'
      ],
      validation: 'El estado cambia a Aprobada o Rechazada y queda registro en histórico.',
      commonError: 'Rechazar sin comentario, acción que el sistema puede bloquear.'
    },
    {
      title: 'Quiero cerrar una estimación',
      objective: 'Finalizar el ciclo de la estimación correctamente.',
      whenToUse: 'Cuando la estimación ya fue revisada y no requiere más cambios.',
      section: 'topic-19',
      audiences: ['reviewer', 'admin'],
      tags: ['cerrar', 'finalizar', 'estado', 'ciclo'],
      steps: [
        'Confirma que el estado actual permite cierre (normalmente Aprobada).',
        'Pulsa Cerrar y revisa mensaje de confirmación.',
        'Verifica que pasa a Cerrada y ya no admite edición operativa.'
      ],
      validation: 'Estado final Cerrada y solo acciones de consulta/comentario disponibles.',
      commonError: 'Intentar cerrar una estimación que no pasó por validación previa.'
    }
  ];

  private getTopicNum(key: HelpTopicKey): string {
    return key.replace('topic-', '');
  }

  getTopicLabel(key: HelpTopicKey): string {
    return this.variables?.[`helpTopic${this.getTopicNum(key)}Label`]
      || this.helpSections.find(s => s.key === key)?.label
      || key;
  }

  getTopicTitle(key: HelpTopicKey): string {
    return this.variables?.[`helpTopic${this.getTopicNum(key)}Title`] || this.getTopicLabel(key);
  }

  getTopicBody(key: HelpTopicKey): SafeHtml {
    const html = this.variables?.[`helpTopic${this.getTopicNum(key)}Body`] || '';
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  getSafeHtml(html: string | undefined): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html || '');
  }

  constructor(
    private service: AuthService,
    private sidenavService: SidenavService,
    private languageService: LanguageService,
    public dialog: MatDialog,
    private router: Router,
    private estimativaService: ApiEstimativaService,
    private sanitizer: DomSanitizer,
    private scrollService: ScrollControlService
  ) { }

  ngOnInit(): void {
    this.scrollService.setScroll(true);

    this.languageService.translations$.subscribe((vars: any) => {
      this.variables = vars;
    });

    this.service.getUserData().subscribe((user) => {
      const role = (user?.role || '').toString().toUpperCase();
      const mappedRole = this.mapUserRoleToGuideRole(role);
      this.autoDetectedGuideRole = mappedRole;
      if (!this.hasManualRoleSelection) {
        this.selectedGuideRole = mappedRole;
      }
    });

  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.syncActiveSectionFromViewport();
      this.setupTopicObserver();
    }, 0);
  }

  ngOnDestroy(): void {
    if (this.topicObserver) {
      this.topicObserver.disconnect();
      this.topicObserver = null;
    }
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    this.syncActiveSectionFromViewport();
  }

  onSidenavToggle() {
    this.isOpen = !this.isOpen;
    this.sidenavService.sideNavState$.next(this.isOpen);
  }

  onCheckboxChange(event: any) {
    this.shouldPinSidenav = event;
    this.sidenavService.shouldPinSidenavState.next(this.shouldPinSidenav);
  }

  downloadPDF(response: any, fileName: any) {
    // Doing it this way allows you to name the file
    var link = document.createElement('a');
    link.href = window.URL.createObjectURL(response);
    link.download = fileName;
    link.click();
  }

  downloadFile(file: string) {
    if (file == 'pt') {
      this.estimativaService.downloadManual('PT').subscribe((data: any) => {
        if (data) {
          this.downloadPDF(data, 'manual_pt')
        }
      })
    }
    else if (file == 'en') {
      this.estimativaService.downloadManual('EN').subscribe((data: any) => {
        if (data) {
          this.downloadPDF(data, 'manual_en')
        }
      })
    }
  }

  searchFilter(event: Event) {
    const filtro = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filtro.trim().toLowerCase();
  }

  get filteredHelpSections() {
    const term = this.helpSearchTerm.trim().toLowerCase();
    const sections = this.helpSections.map(s => ({ ...s, label: this.getTopicLabel(s.key) }));
    if (term) {
      return sections.filter(s =>
        s.label.toLowerCase().includes(term)
      );
    }

    if (this.showAllTopics) {
      return sections;
    }

    return sections.filter((s) => s.featured);
  }

  get hiddenTopicsCount() {
    if (this.helpSearchTerm.trim()) {
      return 0;
    }

    return this.helpSections.filter((section) => !section.featured).length;
  }

  get computedHelpUseCases(): HelpUseCase[] {
    if (!this.variables) return this.helpUseCases;
    return this.helpUseCases.map((uc, i) => {
      const n = String(i + 1).padStart(2, '0');
      const k = 'helpUseCase' + n;
      return {
        ...uc,
        title: this.variables[k + 'Title'] || uc.title,
        objective: this.variables[k + 'Objective'] || uc.objective,
        whenToUse: this.variables[k + 'WhenToUse'] || uc.whenToUse,
        steps: [
          this.variables[k + 'Step1'] || uc.steps[0],
          this.variables[k + 'Step2'] || uc.steps[1],
          this.variables[k + 'Step3'] || uc.steps[2],
        ],
        validation: this.variables[k + 'Validation'] || uc.validation,
        commonError: this.variables[k + 'CommonError'] || uc.commonError,
      };
    });
  }

  get filteredHelpUseCases() {
    const term = this.helpSearchTerm.trim().toLowerCase();
    let result = this.computedHelpUseCases;

    if (this.selectedGuideRole !== 'all') {
      result = result.filter((item) => item.audiences.includes(this.selectedGuideRole));
    }

    if (!term) {
      return result;
    }

    return result.filter((item) =>
      item.title.toLowerCase().includes(term)
      || item.whenToUse.toLowerCase().includes(term)
      || item.objective.toLowerCase().includes(term)
      || item.tags.some((tag) => tag.includes(term))
    );
  }

  setHelpSection(section: HelpTopicKey) {
    this.activeHelpSection = section;
    this.ensureSectionVisibleInSidebar(section);

    setTimeout(() => {
      const element = document.getElementById(section);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  }

  getSectionLabel(section: HelpTopicKey) {
    return this.getTopicLabel(section) || this.variables?.helpSectionFallback || 'Sección';
  }

  toggleTopicsVisibility() {
    this.showAllTopics = !this.showAllTopics;
  }

  setGuideRole(role: QuickGuideRole) {
    this.hasManualRoleSelection = true;
    this.selectedGuideRole = role;
  }

  isRecommendedForDetectedRole(item: HelpUseCase) {
    return this.autoDetectedGuideRole !== 'all' && item.audiences.includes(this.autoDetectedGuideRole);
  }

  get detectedRoleLabel() {
    return this.roleFilterOptions.find((option) => option.key === this.autoDetectedGuideRole)?.label || this.variables?.helpRoleAll || 'Todos';
  }

  get selectedRoleLabel() {
    return this.roleFilterOptions.find((option) => option.key === this.selectedGuideRole)?.label || this.variables?.helpRoleAll || 'Todos';
  }

  get roleContextMessage() {
    const showing = this.variables?.helpRoleContextShowing || 'Mostrando recomendaciones para:';
    const filter = this.variables?.helpRoleContextFilter || 'Filtro actual:';
    const detected = this.variables?.helpRoleContextDetected || 'Rol detectado:';
    if (this.selectedGuideRole === this.autoDetectedGuideRole || this.autoDetectedGuideRole === 'all') {
      return `${showing} ${this.selectedRoleLabel}`;
    }
    return `${filter} ${this.selectedRoleLabel}. ${detected} ${this.detectedRoleLabel}`;
  }

  private mapUserRoleToGuideRole(role: string): QuickGuideRole {
    if (role === 'ADMIN' || role === 'SUPER-ADMIN') {
      return 'admin';
    }

    if (role === 'ARCHITECT' || role === 'ESTIMATION-OFFICE') {
      return 'reviewer';
    }

    if (role === 'USER') {
      return 'estimator';
    }

    return 'all';
  }

  private syncActiveSectionFromViewport() {
    const headings = Array.from(document.querySelectorAll('.help-content h5[id]')) as HTMLElement[];

    if (headings.length === 0) {
      return;
    }

    let currentSection = headings[0].id;

    for (const heading of headings) {
      if (heading.getBoundingClientRect().top <= 180) {
        currentSection = heading.id;
      } else {
        break;
      }
    }

    if (currentSection !== this.activeHelpSection) {
      this.activeHelpSection = currentSection;
    }

    this.ensureSectionVisibleInSidebar(currentSection);
  }

  private setupTopicObserver() {
    const headings = Array.from(document.querySelectorAll('.help-content h5[id]')) as HTMLElement[];

    if (headings.length === 0) {
      return;
    }

    if (this.topicObserver) {
      this.topicObserver.disconnect();
    }

    this.topicObserver = new IntersectionObserver(
      () => {
        this.syncActiveSectionFromViewport();
      },
      {
        root: null,
        // Trigger updates when heading enters the upper part of the viewport.
        rootMargin: '-130px 0px -55% 0px',
        threshold: [0, 0.1, 0.25, 0.5]
      }
    );

    headings.forEach((heading) => this.topicObserver?.observe(heading));
  }

  private ensureSectionVisibleInSidebar(section: HelpTopicKey) {
    const topic = this.helpSections.find((item) => item.key === section);
    if (!topic) {
      return;
    }

    if (!topic.featured && !this.showAllTopics && !this.helpSearchTerm.trim()) {
      this.showAllTopics = true;
    }
  }

}
